import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

interface InvoiceRequest {
  id: string;
  group_id: string;
  group_admin_id: string;
  member_count: number;
  price_per_member: number;
  total_amount: number;
  status: 'pending' | 'generated' | 'cancelled';
  created_at: string;
  generated_at: string | null;
  group: {
    id: string;
    name: string;
  } | null;
  group_admin: {
    id: string;
    full_name: string;
    email: string;
  } | null;
  plan: {
    id: string;
    plan_name: string;
  } | null;
}

interface PayAsYouGoGroup {
  id: string;
  name: string;
  created_by: string;
  group_admin: {
    id: string;
    full_name: string;
    email: string;
  } | null;
  subscription: {
    id: string;
    plan: {
      id: string;
      plan_name: string;
      price_per_member_vatu: number;
    } | null;
  } | null;
  member_count: number;
}

const InvoiceGeneration = () => {
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState<InvoiceRequest[]>([]);
  const [payAsYouGoGroups, setPayAsYouGoGroups] = useState<PayAsYouGoGroup[]>([]);
  const [generating, setGenerating] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'generated'>('pending');
  const [autoGenerating, setAutoGenerating] = useState(false);

  useEffect(() => {
    if (userProfile?.role === 'super_admin') {
      fetchData();
    }
  }, [userProfile, filter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      await Promise.all([fetchPendingRequests(), fetchPayAsYouGoGroups()]);
    } catch (error) {
      console.error('Error fetching invoice data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingRequests = async () => {
    try {
      let query = supabase
        .from('invoice_generation_requests')
        .select(`
          id,
          group_id,
          group_admin_id,
          member_count,
          price_per_member,
          total_amount,
          status,
          created_at,
          generated_at,
          group:group_id (
            id,
            name
          ),
          group_admin:group_admin_id (
            id,
            full_name,
            email
          ),
          plan:plan_id (
            id,
            plan_name
          )
        `)
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;
      setPendingRequests((data || []) as InvoiceRequest[]);
    } catch (error) {
      console.error('Error fetching pending requests:', error);
    }
  };

  const fetchPayAsYouGoGroups = async () => {
    try {
      // Get all groups with pay-as-you-go subscriptions
      const { data: groups, error: groupsError } = await supabase
        .from('groups')
        .select(`
          id,
          name,
          created_by,
          group_admin:created_by (
            id,
            full_name,
            email
          )
        `)
        .eq('is_active', true);

      if (groupsError) throw groupsError;

      // For each group, check if they have pay-as-you-go subscription
      const groupsWithPayg = await Promise.all(
        (groups || []).map(async (group: any) => {
          // Get subscription for group creator
          const { data: subscription } = await supabase
            .from('user_subscriptions')
            .select(`
              id,
              plan:subscription_plans!plan_id (
                id,
                plan_name,
                billing_model,
                price_per_member_vatu
              )
            `)
            .eq('user_id', group.created_by)
            .eq('subscription_status', 'active')
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();

          const plan = subscription?.plan as any;
          if (plan?.billing_model !== 'per_member') {
            return null;
          }

          // Get member count (excluding group creator - handled by RPC function)
          const { data: memberCountData } = await supabase
            .rpc('get_group_member_count', { group_id_param: group.id });

          const memberCount = memberCountData || 0; // RPC function already excludes group creator

          return {
            id: group.id,
            name: group.name,
            created_by: group.created_by,
            group_admin: Array.isArray(group.group_admin) ? group.group_admin[0] : group.group_admin,
            subscription: {
              id: subscription?.id,
              plan: {
                id: plan?.id,
                plan_name: plan?.plan_name,
                price_per_member_vatu: plan?.price_per_member_vatu
              }
            },
            member_count: memberCount
          };
        })
      );

      setPayAsYouGoGroups(groupsWithPayg.filter(g => g !== null) as PayAsYouGoGroup[]);
    } catch (error) {
      console.error('Error fetching pay-as-you-go groups:', error);
    }
  };

  const handleCreateRequest = async (groupId: string) => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc('create_invoice_generation_request', {
        group_id_param: groupId
      });

      if (error) throw error;

      alert('Invoice generation request created successfully');
      await fetchData();
    } catch (error: any) {
      console.error('Error creating invoice request:', error);
      alert('Failed to create invoice request: ' + (error.message || 'Unknown error'));
    }
  };

  const handleGenerateInvoiceDirectly = async (groupId: string) => {
    if (!user) return;

    try {
      setGenerating(groupId);
      
      // First create the request, then immediately generate the invoice
      const { data: requestId, error: requestError } = await supabase.rpc('create_invoice_generation_request', {
        group_id_param: groupId
      });

      if (requestError) throw requestError;

      // Immediately generate invoice from the request
      const { data, error } = await supabase.rpc('generate_invoice_from_request', {
        request_id_param: requestId,
        generated_by_user_id: user.id
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      alert(`Invoice generated successfully! Amount: ${data.amount} VT for ${data.member_count} members`);
      await fetchData();
    } catch (error: any) {
      console.error('Error generating invoice directly:', error);
      alert('Failed to generate invoice: ' + (error.message || 'Unknown error'));
    } finally {
      setGenerating(null);
    }
  };

  const handleGenerateInvoice = async (requestId: string) => {
    if (!user) return;

    try {
      setGenerating(requestId);
      const { data, error } = await supabase.rpc('generate_invoice_from_request', {
        request_id_param: requestId,
        generated_by_user_id: user.id
      });

      if (error) throw error;

      if (data?.error) {
        throw new Error(data.error);
      }

      alert(`Invoice generated successfully! Amount: ${data.amount} VT for ${data.member_count} members`);
      await fetchData();
    } catch (error: any) {
      console.error('Error generating invoice:', error);
      alert('Failed to generate invoice: ' + (error.message || 'Unknown error'));
    } finally {
      setGenerating(null);
    }
  };

  const handleAutoGenerateInvoices = async () => {
    if (!user) return;
    
    if (!confirm('This will automatically generate invoices for all subscriptions with payment due dates that have passed. Continue?')) {
      return;
    }

    try {
      setAutoGenerating(true);
      const { data, error } = await supabase.rpc('auto_generate_invoices_for_due_subscriptions');

      if (error) throw error;

      if (data?.error) {
        alert('Error: ' + data.error);
        return;
      }

      const generated = data?.generated_count || 0;
      const errors = data?.error_count || 0;
      
      if (generated > 0) {
        alert(`Successfully generated ${generated} invoice(s)${errors > 0 ? ` with ${errors} error(s)` : ''}. Notifications have been sent to group creators and admins.`);
      } else {
        alert('No invoices to generate. All subscriptions are up to date.');
      }
      
      await fetchData();
    } catch (error: any) {
      console.error('Error auto-generating invoices:', error);
      alert('Failed to auto-generate invoices: ' + (error.message || 'Unknown error'));
    } finally {
      setAutoGenerating(false);
    }
  };

  if (userProfile?.role !== 'super_admin') {
    return null;
  }

  return (
    <div className="bg-card border border-border rounded-md p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Invoice Generation</h3>
          <p className="text-sm text-muted-foreground">
            Generate invoices for group admins on pay-as-you-go plans
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            💡 Automatic generation runs daily at 2 AM UTC. You can also trigger it manually below.
          </p>
        </div>
        <Icon name="FileText" size={24} className="text-primary" />
      </div>

      {/* Auto-Generate Button */}
      <div className="bg-primary/10 border border-primary/20 rounded-md p-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-1">Automatic Invoice Generation</h4>
            <p className="text-xs text-muted-foreground">
              Generate invoices for all subscriptions with payment due dates that have passed
            </p>
          </div>
          <Button
            onClick={handleAutoGenerateInvoices}
            disabled={autoGenerating}
            iconName={autoGenerating ? 'Loader2' : 'Zap'}
            iconPosition="left"
          >
            {autoGenerating ? 'Generating...' : 'Run Auto-Generation Now'}
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center gap-2">
        <label className="text-xs font-medium text-foreground">Filter:</label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value as any)}
          className="px-3 py-1.5 rounded-md border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="generated">Generated</option>
        </select>
      </div>

      {/* Pending Requests */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Pending Invoice Requests ({pendingRequests.filter(r => r.status === 'pending').length})
        </h4>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="Loader2" size={24} className="animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading...</span>
          </div>
        ) : pendingRequests.filter(r => filter === 'all' || r.status === filter).length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="FileX" size={48} className="mx-auto mb-2 opacity-50" />
            <p>No invoice requests found</p>
          </div>
        ) : (
          <div className="space-y-3">
            {pendingRequests
              .filter(r => filter === 'all' || r.status === filter)
              .map((request) => (
                <div
                  key={request.id}
                  className="border border-border rounded-md p-4 hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm font-medium text-foreground">
                          {request.group?.name || 'Unknown Group'}
                        </span>
                        <span
                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                            request.status === 'pending'
                              ? 'text-warning bg-warning/10'
                              : request.status === 'generated'
                              ? 'text-success bg-success/10'
                              : 'text-muted-foreground bg-muted'
                          }`}
                        >
                          <Icon
                            name={
                              request.status === 'pending'
                                ? 'Clock'
                                : request.status === 'generated'
                                ? 'CheckCircle'
                                : 'XCircle'
                            }
                            size={14}
                          />
                          {request.status.toUpperCase()}
                        </span>
                      </div>
                      <div className="text-sm text-muted-foreground space-y-1">
                        <p>
                          Admin: <span className="font-medium text-foreground">
                            {request.group_admin?.full_name || request.group_admin?.email || 'Unknown'}
                          </span>
                        </p>
                        <p>
                          Plan: <span className="font-medium text-foreground">
                            {request.plan?.plan_name || 'Unknown'}
                          </span>
                        </p>
                        <p>
                          Members: <span className="font-medium text-foreground">{request.member_count}</span>
                          {' × '}
                          <span className="font-medium text-foreground">
                            {request.price_per_member.toLocaleString()} VT
                          </span>
                          {' = '}
                          <span className="font-semibold text-foreground">
                            {request.total_amount.toLocaleString('en-US', { minimumFractionDigits: 2 })} VT
                          </span>
                        </p>
                        <p className="text-xs">
                          Created: {new Date(request.created_at).toLocaleString('en-US')}
                        </p>
                      </div>
                    </div>
                    <div className="ml-4">
                      {request.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => handleGenerateInvoice(request.id)}
                          disabled={generating === request.id}
                          iconName={generating === request.id ? 'Loader2' : 'FileText'}
                          iconPosition="left"
                        >
                          {generating === request.id ? 'Generating...' : 'Generate Invoice'}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Pay-as-You-Go Groups */}
      <div>
        <h4 className="text-sm font-semibold text-foreground mb-3">
          Pay-as-You-Go Groups ({payAsYouGoGroups.length})
        </h4>
        {payAsYouGoGroups.length === 0 ? (
          <div className="text-center py-4 text-muted-foreground text-sm">
            No groups on pay-as-you-go plans found
          </div>
        ) : (
          <div className="space-y-2">
            {payAsYouGoGroups.map((group) => {
              const calculatedAmount =
                (group.subscription?.plan?.price_per_member_vatu || 0) * group.member_count;
              const hasPendingRequest = pendingRequests.some(
                r => r.group_id === group.id && r.status === 'pending'
              );

              return (
                <div
                  key={group.id}
                  className="border border-border rounded-md p-3 flex items-center justify-between"
                >
                  <div className="flex-1">
                    <p className="text-sm font-medium text-foreground">{group.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {group.group_admin?.full_name || group.group_admin?.email} • {group.member_count} members
                      {' × '}
                      {(group.subscription?.plan?.price_per_member_vatu || 0).toLocaleString()} VT
                      {' = '}
                      <span className="font-semibold">
                        {calculatedAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })} VT
                      </span>
                    </p>
                  </div>
                  <div>
                    {hasPendingRequest ? (
                      <span className="text-xs text-muted-foreground">Request pending</span>
                    ) : (
                      <Button
                        size="sm"
                        onClick={() => handleGenerateInvoiceDirectly(group.id)}
                        disabled={generating === group.id}
                        iconName={generating === group.id ? 'Loader2' : 'FileText'}
                        iconPosition="left"
                      >
                        {generating === group.id ? 'Generating...' : 'Generate Invoice'}
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceGeneration;
